// src/components/Prompts/Prompts.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
 
const C = {
    primary:          '#6a37d4',
    primaryLight:     '#ae8dff',
    primaryContainer: '#ede8ff',
    secondary:        '#006947',
    secondaryContainer: '#d1fae5',
    tertiary:         '#6f5900',
    tertiaryContainer:'#fed01b',
    surface:          '#f3f6ff',
    surfaceLow:       '#eaf1ff',
    surfaceHigh:      '#d6e3f9',
    surfaceHighest:   '#cfdef5',
    surfaceLowest:    '#ffffff',
    onBackground:     '#252f3c',
    onSurface:        '#252f3c',
    onSurfaceVariant: '#525c6a',
    outlineVariant:   '#a4aebe',
};
 
type Genre = 'All' | 'Fantasy' | 'Mystery' | 'Sci-Fi' | 'Adventure' | 'Comedy' | 'Scary' | 'Historical Fiction' | 'Realistic';
 
interface Prompt {
    id: number;
    title: string;
    body: string;
    genre: Exclude<Genre, 'All'>;
    emoji: string;
    color: string;
    textColor: string;
}

const FEATURED_PROMPTS = [
    {
      title: 'The Clockwork Knight',
      body: 'In a kingdom where every citizen is powered by gears and springs, one rusty knight discovers a heart made of real flesh and blood. How does he hide his secret from the Grand Tinkerer?',
      emoji: '⚙️',
      genre: 'Fantasy' as const,
    },
    {
      title: 'The Ink That Moves',
      body: 'The words in your story have started rearranging themselves overnight. By morning the plot is completely different — and somehow better. Something is editing your work.',
      emoji: '✍️',
      genre: 'Mystery' as const,
    },
    {
      title: 'The Last Beekeeper',
      body: "In a world where bees went silent twenty years ago, you discover a hidden hive in the walls of your grandmother's house. Keeping it secret might be the most important thing you ever do.",
      emoji: '🍯',
      genre: 'Realistic' as const,
    },
    {
      title: 'Understudy',
      body: "The lead in the school play gets sick on opening night. You know every word — you've been secretly rehearsing for months. Nobody knows why.",
      emoji: '🎭',
      genre: 'Realistic' as const,
    },
  ];
  
  const weekNumber = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000));
  const featuredPrompt = FEATURED_PROMPTS[weekNumber % FEATURED_PROMPTS.length];
 
const ALL_PROMPTS: Prompt[] = [
    {
        id: 1,
        title: 'The Door in the Oak',
        body: 'You find a key in your cereal box that perfectly fits a tiny door at the base of the oldest tree in your neighbourhood.',
        genre: 'Fantasy',
        emoji: '🌳',
        color: '#f3e8ff',
        textColor: C.primary,
    },
    {
        id: 2,
        title: 'Neon Nova-9',
        body: 'The space station computer starts telling jokes — but the jokes are actually coded warnings about an incoming asteroid.',
        genre: 'Sci-Fi',
        emoji: '🚀',
        color: C.secondaryContainer,
        textColor: C.secondary,
    },
    {
        id: 3,
        title: 'The Blank Ledger',
        body: 'Every book in the school library went blank overnight — except the diary you just started writing in.',
        genre: 'Mystery',
        emoji: '📖',
        color: '#dbeafe',
        textColor: '#1d4ed8',
    },
    {
        id: 4,
        title: 'The Last Dragon Egg',
        body: 'You are the royal egg-keeper. This morning the egg is warm. It has never been warm before.',
        genre: 'Fantasy',
        emoji: '🥚',
        color: '#f3e8ff',
        textColor: C.primary,
    },
    {
        id: 5,
        title: 'My Robot Best Friend',
        body: 'The new kid at school never eats lunch and always knows the exact time. You are starting to wonder if they are actually a robot.',
        genre: 'Sci-Fi',
        emoji: '🤖',
        color: C.secondaryContainer,
        textColor: C.secondary,
    },
    {
        id: 6,
        title: 'The Missing Trophy',
        body: 'The championship trophy vanished from the locked display case. Three people have a key. One of them is you.',
        genre: 'Mystery',
        emoji: '🏆',
        color: '#dbeafe',
        textColor: '#1d4ed8',
    },
    {
        id: 7,
        title: 'The New Kid Who Glows',
        body: 'The new student only glows when she is nervous. Today is her first day and the whole cafeteria can see it.',
        genre: 'Realistic',
        emoji: '✨',
        color: C.tertiaryContainer,
        textColor: C.tertiary,
    },
    {
        id: 8,
        title: 'Cloud Shepherd',
        body: 'Your job is to keep the storm clouds from drifting over the village. Today one escapes and you have to chase it across three kingdoms.',
        genre: 'Fantasy',
        emoji: '☁️',
        color: '#f3e8ff',
        textColor: C.primary,
    },
    {
        id: 9,
        title: 'The Time Capsule Letter',
        body: 'You open a time capsule buried ten years ago and find a letter addressed specifically to you — written in your own handwriting.',
        genre: 'Mystery',
        emoji: '📬',
        color: '#dbeafe',
        textColor: '#1d4ed8',
    },
    {
        id: 10,
        title: 'Moving Day on Mars',
        body: 'Your family is the first to move into the new neighbourhood on Mars. Your next-door neighbour knocks to borrow a cup of moon-sugar.',
        genre: 'Sci-Fi',
        emoji: '🪐',
        color: C.secondaryContainer,
        textColor: C.secondary,
    },
    {
        id: 11,
        title: 'The Spelling Bee Champion',
        body: 'You have practised for months. Then, standing at the microphone, you realise the word they give you does not exist in any dictionary.',
        genre: 'Realistic',
        emoji: '🐝',
        color: C.tertiaryContainer,
        textColor: C.tertiary,
    },
    {
        id: 12,
        title: 'The Invisible Bridge',
        body: 'The old map shows a bridge over the canyon. There is no bridge there. But when you step forward, you do not fall.',
        genre: 'Fantasy',
        emoji: '🌉',
        color: '#f3e8ff',
        textColor: C.primary,
    },
    
    // ── New prompts: add to ALL_PROMPTS after id 12 ──────────────────

// Fantasy (ids 13–14)
{
    id: 13,
    title: 'The Whispering Map',
    body: 'The old map on your wall has started giving directions — to places that do not exist yet.',
    genre: 'Fantasy',
    emoji: '🗺️',
    color: '#f3e8ff',
    textColor: C.primary,
},
{
    id: 14,
    title: 'The Colour Thief',
    body: 'Someone has been stealing colours from the world. The sky is grey. The flowers are white. Only you can see who is doing it.',
    genre: 'Fantasy',
    emoji: '🎨',
    color: '#f3e8ff',
    textColor: C.primary,
},

// Mystery (ids 15–16)
{
    id: 15,
    title: "The Lighthouse Keeper's Log",
    body: 'The lighthouse has been empty for thirty years. The log on the desk has an entry from last night — in fresh ink.',
    genre: 'Mystery',
    emoji: '🏮',
    color: '#dbeafe',
    textColor: '#1d4ed8',
},
{
    id: 16,
    title: 'The Switched Lunches',
    body: 'Someone swapped your lunch with a note that reads: "Meet me at the oak tree. Come alone. Tell no one."',
    genre: 'Mystery',
    emoji: '🥪',
    color: '#dbeafe',
    textColor: '#1d4ed8',
},

// Sci-Fi (ids 17–18)
{
    id: 17,
    title: 'The Memory Merchant',
    body: 'In this city, you can buy and sell memories. You find one for sale that is definitely yours — but you have no idea when you sold it.',
    genre: 'Sci-Fi',
    emoji: '🧠',
    color: C.secondaryContainer,
    textColor: C.secondary,
},
{
    id: 18,
    title: 'Signal',
    body: 'Every night at exactly 11:11pm your watch beeps once. Scientists say it is impossible. It has been happening for a year.',
    genre: 'Sci-Fi',
    emoji: '📡',
    color: C.secondaryContainer,
    textColor: C.secondary,
},

// Adventure (ids 19–23)
{
    id: 19,
    title: 'The Rope Bridge',
    body: 'The rope bridge across the ravine is the only way home before dark. Halfway across, you hear it starting to snap.',
    genre: 'Adventure',
    emoji: '🌉',
    color: '#dcfce7',
    textColor: '#15803d',
},
{
    id: 20,
    title: 'Snowbound',
    body: 'Your class trip to the mountain cabin turns into a survival situation when the storm rolls in and the adults lose the map.',
    genre: 'Adventure',
    emoji: '🏔️',
    color: '#dcfce7',
    textColor: '#15803d',
},
{
    id: 21,
    title: 'The Uncharted Island',
    body: 'The boat drifts off course in the night. By morning you are anchored beside an island that appears on no map.',
    genre: 'Adventure',
    emoji: '🏝️',
    color: '#dcfce7',
    textColor: '#15803d',
},
{
    id: 22,
    title: 'First to the Summit',
    body: 'You and your rival are both climbing for the same record. At the final ridge, you find them — hurt, and unable to go on alone.',
    genre: 'Adventure',
    emoji: '⛰️',
    color: '#dcfce7',
    textColor: '#15803d',
},
{
    id: 23,
    title: 'The River Runs Wrong',
    body: 'You and your cousin are kayaking downstream when you realise the river is flowing in the wrong direction — and getting faster.',
    genre: 'Adventure',
    emoji: '🛶',
    color: '#dcfce7',
    textColor: '#15803d',
},

// Realistic (ids 24–26)
{
    id: 24,
    title: 'The Empty Chair',
    body: 'Your best friend has been absent for two weeks. The teacher acts like everything is normal. Nobody will tell you why.',
    genre: 'Realistic',
    emoji: '🪑',
    color: C.tertiaryContainer,
    textColor: C.tertiary,
},
{
    id: 25,
    title: 'Three Seconds',
    body: 'You have three seconds to decide whether to tell the truth or cover for your friend. Write what happens in the next hour.',
    genre: 'Realistic',
    emoji: '⏱️',
    color: C.tertiaryContainer,
    textColor: C.tertiary,
},
{
    id: 26,
    title: 'New School, Same Me',
    body: "First day at a new school and someone has already decided who you are. They are completely wrong — but also kind of right.",
    genre: 'Realistic',
    emoji: '🎒',
    color: C.tertiaryContainer,
    textColor: C.tertiary,
},

// Funny (ids 27–30)
{
    id: 27,
    title: 'Class Pet Chaos',
    body: 'The class hamster escapes the morning of the school inspection. You are the only one who knows where it went.',
    genre: 'Comedy',
    emoji: '🐹',
    color: '#fff7ed',
    textColor: '#c2410c',
},
{
    id: 28,
    title: 'Too Many Wishes',
    body: 'You find a lamp with a genie who grants unlimited wishes — but each one backfires in the most inconvenient way possible.',
    genre: 'Comedy',
    emoji: '🪔',
    color: '#fff7ed',
    textColor: '#c2410c',
},
{
    id: 29,
    title: 'The Shrinking Spell',
    body: 'The magic went wrong and now you are the size of a grape. Your dog has no idea and your family is about to sit down for dinner.',
    genre: 'Comedy',
    emoji: '🍇',
    color: '#fff7ed',
    textColor: '#c2410c',
},
{
    id: 30,
    title: 'Wrong Costume',
    body: 'You show up to the costume party as a famous villain. So does everyone else. The real villain is furious.',
    genre: 'Comedy',
    emoji: '🎭',
    color: '#fff7ed',
    textColor: '#c2410c',
},
];
 
const RANDOM_PROMPTS = [
    'A library book keeps returning to your shelf no matter how many times you return it.',
    'You wake up and every adult in the world has forgotten how to read.',
    'Your shadow starts walking in the wrong direction.',
    'The dragon in your village is not dangerous — she is just extremely lonely.',
    'You discover your grandmother has been secretly corresponding with aliens for forty years.',
    'Every time you lie, a small flower grows from the top of your head.',
    'The school loses power and the only light comes from the new student.',
    'A message in a bottle asks you to find the person who sent it — the date on the letter is tomorrow.',
    'You wake up and every clock in the world reads a different time.',
    'The school librarian has a door in her office that was not there last week.',
    'Your reflection waves at you — half a second before you do.',
    'The new neighbours never have their lights on. But they are always home.',
    'You find a coin that has not been minted yet.',
    'The last page of every book you open is blank — except for a single sentence meant for you.',
    'Your bicycle takes you somewhere different every time you close your eyes on the way home.',
    'A bird lands on your windowsill and says one word. It is your name.',
    'The painting in the school hallway has been changing. Today someone new is standing in it.',
    'You receive a birthday card signed by your future self. The advice is terrible.',
];
 
const GENRES: { label: Genre; emoji: string; bg: string; text: string }[] = [
    { label: 'All',                emoji: '🌟', bg: C.primaryContainer,    text: C.primary   },
    { label: 'Fantasy',            emoji: '🏰', bg: '#f3e8ff',             text: C.primary   },
    { label: 'Mystery',            emoji: '🔍', bg: '#dbeafe',             text: '#1d4ed8'   },
    { label: 'Sci-Fi',             emoji: '🚀', bg: C.secondaryContainer,  text: C.secondary },
    { label: 'Adventure',          emoji: '🌲', bg: '#dcfce7',             text: '#15803d'   },
    { label: 'Comedy',             emoji: '😂', bg: '#fff7ed',             text: '#c2410c'   },
    { label: 'Scary',              emoji: '👻', bg: '#f3e8ff',             text: '#6b21a8'   },
    { label: 'Historical Fiction', emoji: '📜', bg: '#fef3c7',             text: '#92400e'   },
    { label: 'Realistic',          emoji: '😊', bg: C.tertiaryContainer,   text: C.tertiary  },
];
 
const Prompts: React.FC = () => {
    const navigate = useNavigate();
    const [activeGenre, setActiveGenre] = useState<Genre>('All');
    const [randomPrompt, setRandomPrompt] = useState<string | null>(null);
    const [shaking, setShaking] = useState(false);
    const [savedIds, setSavedIds] = useState<number[]>([]);
 
    const filtered = activeGenre === 'All'
        ? ALL_PROMPTS
        : ALL_PROMPTS.filter(p => p.genre === activeGenre);
 
    const handleStartWriting = (prompt?: string) => {
        navigate('/regular-book-editor', { state: { prompt } });
    };
 
    const handleShake = () => {
        if (shaking) return;
        setShaking(true);
        setRandomPrompt(null);
        setTimeout(() => {
            const pick = RANDOM_PROMPTS[Math.floor(Math.random() * RANDOM_PROMPTS.length)];
            setRandomPrompt(pick);
            setShaking(false);
        }, 600);
    };
 
    const toggleSave = (id: number) => {
        setSavedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };
 
    return (
        <div style={{ minHeight: '100vh', background: C.surface, fontFamily: "'Plus Jakarta Sans', sans-serif", paddingBottom: '80px' }}>
 
            {/* ── Hero header ──────────────────────────────────────────────── */}
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '48px 32px 0' }}>
                <h1 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '3.5rem', fontWeight: '900', color: C.primary, margin: '0 0 10px 0', lineHeight: '1.1' }}>
                    Prompt Garden
                </h1>
                <p style={{ fontSize: '1.1rem', color: C.onSurfaceVariant, margin: 0, maxWidth: '560px', lineHeight: '1.65' }}>
                    Plant a tiny idea and watch a story bloom. Find your next big adventure among our enchanted seeds.
                </p>
            </div>
 
            {/* ── Weekly featured prompt ───────────────────────────────────── */}
            <div style={{ maxWidth: '1200px', margin: '32px auto 0', padding: '0 32px' }}>
                <div style={{ background: C.surfaceLowest, borderRadius: '2rem', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: '340px' }}>
                    {/* Left */}
                    <div style={{ padding: '48px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ background: C.tertiaryContainer, color: C.tertiary, padding: '4px 14px', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: "'Epilogue', sans-serif" }}>
                                Weekly Feature
                            </span>
                            <span style={{ color: C.secondary, fontWeight: '700', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                ✨ Fresh Pick
                            </span>
                        </div>
                        <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '2.4rem', fontWeight: '900', color: C.onBackground, margin: 0, lineHeight: '1.15' }}>
                            The Clockwork Knight
                        </h2>
                        <p style={{ fontSize: '1rem', color: C.onSurfaceVariant, margin: 0, lineHeight: '1.7' }}>
                            In a kingdom where every citizen is powered by gears and springs, one rusty knight discovers a heart made of real flesh and blood. How does he hide his secret from the Grand Tinkerer?
                        </p>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            <button
                                onClick={() => handleStartWriting('In a kingdom where every citizen is powered by gears and springs, one rusty knight discovers a heart made of real flesh and blood.')}
                                style={{ background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`, color: '#fff', border: 'none', borderRadius: '9999px', padding: '13px 28px', fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", boxShadow: `0 4px 14px ${C.primary}35`, transition: 'opacity 0.15s, transform 0.15s', display: 'flex', alignItems: 'center', gap: '8px' }}
                                onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                            >
                                ✏️ Start Writing
                            </button>
                            <button
                                style={{ background: C.surfaceLow, color: C.primary, border: 'none', borderRadius: '9999px', padding: '13px 28px', fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'background 0.15s' }}
                                onMouseEnter={e => { e.currentTarget.style.background = C.surfaceHighest; }}
                                onMouseLeave={e => { e.currentTarget.style.background = C.surfaceLow; }}
                            >
                                Save for Later
                            </button>
                        </div>
                    </div>
                    {/* Right — decorative cover art placeholder */}
                    <div style={{ background: `linear-gradient(135deg, #2a1060 0%, #6a37d4 60%, #ae8dff 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8rem', position: 'relative', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(255,255,255,0.12) 0%, transparent 40%)' }} />
                        ⚙️
                    </div>
                </div>
            </div>
 
            {/* ── Genre filters ────────────────────────────────────────────── */}
            <div style={{ maxWidth: '1200px', margin: '48px auto 0', padding: '0 32px' }}>
                <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.4rem', fontWeight: '900', color: C.onBackground, margin: '0 0 20px 0' }}>
                    Browse by Genre
                </h3>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {GENRES.map(g => (
                        <button
                            key={g.label}
                            onClick={() => setActiveGenre(g.label)}
                            style={{
                                background: activeGenre === g.label ? g.bg : C.surfaceLowest,
                                color: activeGenre === g.label ? g.text : C.onSurfaceVariant,
                                border: `2px solid ${activeGenre === g.label ? g.text + '40' : C.surfaceHigh}`,
                                borderRadius: '9999px',
                                padding: '10px 22px',
                                fontSize: '0.9rem', fontWeight: '700',
                                cursor: 'pointer',
                                fontFamily: "'Epilogue', sans-serif",
                                transition: 'all 0.15s',
                                display: 'flex', alignItems: 'center', gap: '6px',
                            }}
                            onMouseEnter={e => { if (activeGenre !== g.label) e.currentTarget.style.background = C.surfaceLow; }}
                            onMouseLeave={e => { if (activeGenre !== g.label) e.currentTarget.style.background = C.surfaceLowest; }}
                        >
                            {g.emoji} {g.label}
                        </button>
                    ))}
                </div>
            </div>
 
            {/* ── Prompt grid ──────────────────────────────────────────────── */}
            <div style={{ maxWidth: '1200px', margin: '36px auto 0', padding: '0 32px' }}>
                <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.4rem', fontWeight: '900', color: C.onBackground, margin: '0 0 20px 0' }}>
                    Daily Seeds
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
                    {filtered.map(prompt => (
                        <div
                            key={prompt.id}
                            style={{ background: C.surfaceLowest, borderRadius: '24px', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', transition: 'box-shadow 0.25s, transform 0.25s', cursor: 'default' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 12px 32px rgba(0,0,0,0.12)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                        >
                            {/* Cover area */}
                            <div style={{ height: '140px', background: prompt.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '4rem', position: 'relative' }}>
                                {prompt.emoji}
                                {/* Genre badge */}
                                <div style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)', color: prompt.textColor, padding: '3px 10px', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: '700', fontFamily: "'Epilogue', sans-serif" }}>
                                    {prompt.genre}
                                </div>
                                {/* Save button */}
                                <button
                                    onClick={() => toggleSave(prompt.id)}
                                    style={{ position: 'absolute', top: '8px', left: '10px', background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.85rem', transition: 'transform 0.15s' }}
                                    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.15)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                                >
                                    {savedIds.includes(prompt.id) ? '❤️' : '🤍'}
                                </button>
                            </div>
 
                            {/* Body */}
                            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', flexGrow: 1, gap: '8px' }}>
                                <h4 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '1rem', color: C.onBackground, margin: 0 }}>
                                    {prompt.title}
                                </h4>
                                <p style={{ fontSize: '0.85rem', color: C.onSurfaceVariant, margin: 0, lineHeight: '1.6', flexGrow: 1 }}>
                                    {prompt.body}
                                </p>
                                <button
                                    onClick={() => handleStartWriting(prompt.body)}
                                    style={{ marginTop: '12px', width: '100%', background: C.surfaceLow, color: C.primary, border: 'none', borderRadius: '12px', padding: '11px', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = C.primary; e.currentTarget.style.color = '#fff'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = C.surfaceLow; e.currentTarget.style.color = C.primary; }}
                                >
                                    🌱 Plant this Seed
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
 
            {/* ── Random prompt shaker ─────────────────────────────────────── */}
            <div style={{ maxWidth: '1200px', margin: '48px auto 0', padding: '0 32px' }}>
                <div style={{ background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`, borderRadius: '2.5rem', padding: '4px' }}>
                    <div style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)', borderRadius: '2.3rem', padding: '48px 32px', textAlign: 'center' }}>
                        <div style={{ width: '80px', height: '80px', background: '#fff', borderRadius: '50%', margin: '0 auto 24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
                            🌪️
                        </div>
                        <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.8rem', color: '#fff', margin: '0 0 8px 0' }}>
                            Feeling Stuck?
                        </h3>
                        <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '1rem', margin: '0 0 28px 0', lineHeight: '1.6' }}>
                            Let the ink-magic guide your hand. Shake the bottle for a surprise!
                        </p>
 
                        {randomPrompt && (
                            <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '16px', padding: '20px 28px', marginBottom: '24px', maxWidth: '560px', margin: '0 auto 24px' }}>
                                <p style={{ color: '#fff', fontSize: '1rem', lineHeight: '1.7', margin: 0, fontStyle: 'italic' }}>
                                    {randomPrompt}
                                </p>
                                <button
                                    onClick={() => handleStartWriting(randomPrompt)}
                                    style={{ marginTop: '16px', background: '#fff', color: C.primary, border: 'none', borderRadius: '9999px', padding: '10px 24px', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'opacity 0.15s' }}
                                    onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
                                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                                >
                                    ✏️ Write this one!
                                </button>
                            </div>
                        )}
 
                        <button
                            onClick={handleShake}
                            disabled={shaking}
                            style={{
                                background: '#fff', color: C.primary,
                                border: 'none', borderRadius: '9999px',
                                padding: '16px 40px', fontSize: '1.1rem', fontWeight: '900',
                                cursor: shaking ? 'not-allowed' : 'pointer',
                                fontFamily: "'Epilogue', sans-serif",
                                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                transition: 'transform 0.15s, opacity 0.15s',
                                animation: shaking ? 'promptShake 0.5s ease' : 'none',
                                opacity: shaking ? 0.7 : 1,
                            }}
                            onMouseEnter={e => { if (!shaking) e.currentTarget.style.transform = 'scale(1.05)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                        >
                            {shaking ? '✨ Shaking...' : '🫙 Shake for a Random Prompt!'}
                        </button>
                    </div>
                </div>
            </div>
 
            <style>{`
                @keyframes promptShake {
                    0%, 100% { transform: translateX(0) rotate(0deg); }
                    20%       { transform: translateX(-6px) rotate(-3deg); }
                    40%       { transform: translateX(6px)  rotate(3deg); }
                    60%       { transform: translateX(-4px) rotate(-2deg); }
                    80%       { transform: translateX(4px)  rotate(2deg); }
                }
            `}</style>
        </div>
    );
};
 
export default Prompts;